import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Hoja } from "@/components/Hoja";
import { Cover } from "@/components/Cover";
import { EMOTIONS, countWords, moodEmotion } from "@/lib/emotions";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const key = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function monthStats(entries, year, month) {
  const inMonth = entries.filter((e) => { const d = new Date(e.created_at); return d.getFullYear() === year && d.getMonth() === month; });
  const days = new Set(inMonth.map((e) => key(new Date(e.created_at))));
  const words = inMonth.reduce((n, e) => n + countWords(e.body), 0);
  const all = new Set(entries.map((e) => key(new Date(e.created_at))));
  let streak = 0;
  // streak anchored at today for the current month, at the month's last day otherwise
  const today = new Date();
  const monthEnd = new Date(year, month + 1, 0);
  const d = monthEnd < today ? monthEnd : today;
  if (!all.has(key(d))) d.setDate(d.getDate() - 1);
  if (new Date(year, month, 1) <= today) for (; all.has(key(d)); d.setDate(d.getDate() - 1)) streak += 1;
  const byDay = {};
  [...inMonth].reverse().forEach((e) => { byDay[key(new Date(e.created_at))] = e; });
  const counts = EMOTIONS.map((em) => ({ ...em, count: inMonth.filter((e) => e.moods.some((m) => moodEmotion(m) === em.id)).length }))
    .filter((em) => em.count > 0).sort((a, b) => b.count - a.count);
  return { byDay, counts, items: [[inMonth.length, inMonth.length === 1 ? "entrada este mes" : "entradas este mes"], [days.size, days.size === 1 ? "día que escribiste" : "días que escribiste"], [words, "palabras"], [streak, streak === 1 ? "día seguido" : "días seguidos"]] };
}

const NavButton = ({ label, onClick, testId, children }) => (
  <button className="h-9 w-9 rounded-full border-[1.5px] border-tinta inline-flex items-center justify-center hover:bg-resaltado transition-colors"
    aria-label={label} onClick={onClick} data-testid={testId}>{children}</button>
);

const MoodBars = ({ counts }) => {
  const max = counts[0]?.count || 1;
  return (
    <div className="mt-12" data-testid="mood-bars">
      <p className="text-[15px] font-medium text-tinta">Cómo marcaste tus días</p>
      {counts.length === 0 && <p className="text-[15px] text-sec mt-3">Este mes todavía no marcas emociones.</p>}
      <div className="mt-4 flex flex-col gap-3 max-w-xl">
        {counts.map((em) => (
          <div key={em.id} className="grid grid-cols-[110px_minmax(0,1fr)_28px] items-center gap-3" data-testid={`mood-bar-${em.id}`}>
            <span className="text-[15px] text-tinta">{em.name}</span>
            <span className="h-3 rounded-full bg-superficie overflow-hidden">
              <span className="block h-full rounded-full" style={{ width: `${(em.count / max) * 100}%`, background: em.color }} />
            </span>
            <span className="text-[15px] text-sec text-right" data-testid={`mood-count-${em.id}`}>{em.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DayCell = ({ day, entry, isToday, onOpen }) => {
  const border = isToday ? "border-dashed border-acento border-[1.5px] text-tinta font-semibold" : "border-linea text-meta";
  const cls = `relative h-12 lg:h-20 rounded-[8px] border overflow-hidden text-xs text-left ${border}`;
  if (!entry) return <div className={`${cls} bg-hoja p-1.5 lg:p-2`} data-testid={`day-${day}`}>{day}</div>;
  return (
    <button type="button" onClick={onOpen} className={`${cls} hover:opacity-85 transition-opacity`} data-testid={`day-${day}`} aria-label={`Abrir la entrada del ${day}`}>
      <Cover id={entry.id} words={countWords(entry.body)} moods={entry.moods} className="absolute inset-0" />
      <span className="relative block p-1.5 lg:p-2 text-tinta">{day}</span>
    </button>
  );
};

export default function MiMes() {
  const { entries } = useOutletContext();
  const navigate = useNavigate();
  const [view, setView] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const year = view.getFullYear(), month = view.getMonth();
  const { byDay, counts, items } = monthStats(entries, year, month);
  const offset = (view.getDay() + 6) % 7;
  const total = new Date(year, month + 1, 0).getDate();
  const todayKey = key(new Date());
  const title = `${view.toLocaleDateString("es-MX", { month: "long" })} ${year}`;

  return (
    <Hoja testId="screen-mi-mes" title={title} aside={
      <div className="flex gap-2 shrink-0 pt-2">
        <NavButton label="Mes anterior" testId="month-prev" onClick={() => setView(new Date(year, month - 1, 1))}><ChevronLeft size={16} /></NavButton>
        <NavButton label="Mes siguiente" testId="month-next" onClick={() => setView(new Date(year, month + 1, 1))}><ChevronRight size={16} /></NavButton>
      </div>}>
      <div className="grid grid-cols-2 sm:flex sm:gap-10 gap-y-5" data-testid="month-stats">
        {items.map(([n, label]) => (
          <div key={label}><p className="font-serif text-[34px] leading-none text-tinta">{n}</p><p className="text-[15px] text-sec mt-2">{label}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5 lg:gap-2 mt-10" data-testid="month-calendar">
        {DAYS.map((d) => <p key={d} className="eyebrow text-center text-xs pb-2">{d}</p>)}
        {Array.from({ length: offset }, (_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: total }, (_, i) => {
          const k = `${year}-${month}-${i + 1}`;
          const entry = byDay[k];
          return <DayCell key={k} day={i + 1} entry={entry} isToday={k === todayKey} onOpen={() => navigate(`/escribir/${entry.id}`)} />;
        })}
      </div>
      <MoodBars counts={counts} />
      <p className="text-[13px] text-sec mt-12" data-testid="month-footnote">
        Estos datos son solo lo que tú elegiste en cada entrada. Pensive no analiza ni interpreta lo que escribes.
      </p>
    </Hoja>
  );
}
